import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Mail, Lock, User, X, Loader2, ArrowLeft, CheckCircle, Eye, EyeOff, MapPin, Globe } from 'lucide-react';
import { API_URL as API } from '../config/api';

const Auth = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { checkAuth, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  
  // Password visibility toggles
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Page-specific translations
  const pageTexts = {
    fr: {
      login: "Connexion",
      register: "Inscription",
      email: "Email",
      password: "Mot de passe",
      name: "Nom complet",
      city: "Ville",
      country: "Pays",
      confirmPassword: "Confirmer le mot de passe",
      loginButton: "Se connecter",
      registerButton: "S'inscrire",
      orContinueWith: "Ou continuer avec",
      emailPlaceholder: "votre@email.com",
      namePlaceholder: "Jean Dupont",
      cityPlaceholder: "Port-au-Prince",
      countryPlaceholder: "Haïti",
      fillAllFields: "Veuillez remplir tous les champs",
      passwordsNotMatch: "Les mots de passe ne correspondent pas",
      passwordMinLength: "Le mot de passe doit contenir au moins 6 caractères",
      loginSuccess: "Connexion réussie !",
      loginError: "Erreur de connexion",
      registerSuccess: "Inscription réussie ! Connexion automatique...",
      registerError: "Erreur d'inscription",
      forgotPassword: "Mot de passe oublié ?",
      forgotPasswordTitle: "Réinitialiser votre mot de passe",
      forgotPasswordDesc: "Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.",
      sendResetLink: "Envoyer le lien",
      sending: "Envoi en cours...",
      emailSent: "Email envoyé !",
      emailSentDesc: "Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.",
      backToLogin: "Retour à la connexion",
      close: "Fermer"
    },
    en: {
      login: "Login",
      register: "Register",
      email: "Email",
      password: "Password",
      name: "Full name",
      city: "City",
      country: "Country",
      confirmPassword: "Confirm password",
      loginButton: "Log in",
      registerButton: "Sign up",
      orContinueWith: "Or continue with",
      emailPlaceholder: "your@email.com",
      namePlaceholder: "John Doe",
      cityPlaceholder: "Miami",
      countryPlaceholder: "USA",
      fillAllFields: "Please fill in all fields",
      passwordsNotMatch: "Passwords do not match",
      passwordMinLength: "Password must be at least 6 characters",
      loginSuccess: "Login successful!",
      loginError: "Login error",
      registerSuccess: "Registration successful! Logging in...",
      registerError: "Registration error",
      forgotPassword: "Forgot password?",
      forgotPasswordTitle: "Reset your password",
      forgotPasswordDesc: "Enter your email address and we'll send you a link to reset your password.",
      sendResetLink: "Send reset link",
      sending: "Sending...",
      emailSent: "Email sent!",
      emailSentDesc: "If an account exists with this email, you will receive a reset link.",
      backToLogin: "Back to login",
      close: "Close"
    },
    ht: {
      login: "Konekte",
      register: "Enskri",
      email: "Imel",
      password: "Modpas",
      name: "Non konplè",
      confirmPassword: "Konfime modpas",
      loginButton: "Konekte",
      registerButton: "Enskri",
      orContinueWith: "Oswa kontinye ak",
      emailPlaceholder: "imel@ou.com",
      namePlaceholder: "Jan Batis",
      fillAllFields: "Tanpri ranpli tout chan yo",
      passwordsNotMatch: "Modpas yo pa matche",
      passwordMinLength: "Modpas la dwe gen omwen 6 karaktè",
      loginSuccess: "Koneksyon reyisi!",
      loginError: "Erè koneksyon",
      registerSuccess: "Enskripsyon reyisi! Koneksyon otomatik...",
      registerError: "Erè enskripsyon",
      forgotPassword: "Bliye modpas?",
      forgotPasswordTitle: "Reyinisyalize modpas ou",
      forgotPasswordDesc: "Antre adrès imel ou epi nou pral voye yon lyen pou reyinisyalize modpas ou.",
      sendResetLink: "Voye lyen",
      sending: "Voye...",
      emailSent: "Imel voye!",
      emailSentDesc: "Si yon kont egziste ak imel sa a, ou pral resevwa yon lyen reyinisyalizasyon.",
      backToLogin: "Retounen nan koneksyon",
      close: "Fèmen"
    },
    es: {
      login: "Iniciar sesión",
      register: "Registrarse",
      email: "Correo electrónico",
      password: "Contraseña",
      name: "Nombre completo",
      confirmPassword: "Confirmar contraseña",
      loginButton: "Iniciar sesión",
      registerButton: "Registrarse",
      orContinueWith: "O continuar con",
      emailPlaceholder: "tu@correo.com",
      namePlaceholder: "Juan Pérez",
      fillAllFields: "Por favor complete todos los campos",
      passwordsNotMatch: "Las contraseñas no coinciden",
      passwordMinLength: "La contraseña debe tener al menos 6 caracteres",
      loginSuccess: "¡Inicio de sesión exitoso!",
      loginError: "Error de inicio de sesión",
      registerSuccess: "¡Registro exitoso! Iniciando sesión...",
      registerError: "Error de registro",
      forgotPassword: "¿Olvidó su contraseña?",
      forgotPasswordTitle: "Restablecer su contraseña",
      forgotPasswordDesc: "Ingrese su correo electrónico y le enviaremos un enlace para restablecer su contraseña.",
      sendResetLink: "Enviar enlace",
      sending: "Enviando...",
      emailSent: "¡Correo enviado!",
      emailSentDesc: "Si existe una cuenta con este correo, recibirá un enlace de restablecimiento.",
      backToLogin: "Volver al inicio de sesión",
      close: "Cerrar"
    },
    pt: {
      login: "Entrar",
      register: "Registrar",
      email: "Email",
      password: "Senha",
      name: "Nome completo",
      confirmPassword: "Confirmar senha",
      loginButton: "Entrar",
      registerButton: "Registrar",
      orContinueWith: "Ou continuar com",
      emailPlaceholder: "seu@email.com",
      namePlaceholder: "João Silva",
      fillAllFields: "Por favor preencha todos os campos",
      passwordsNotMatch: "As senhas não coincidem",
      passwordMinLength: "A senha deve ter pelo menos 6 caracteres",
      loginSuccess: "Login realizado com sucesso!",
      loginError: "Erro de login",
      registerSuccess: "Registro realizado com sucesso! Fazendo login...",
      registerError: "Erro de registro",
      forgotPassword: "Esqueceu a senha?",
      forgotPasswordTitle: "Redefinir sua senha",
      forgotPasswordDesc: "Digite seu email e enviaremos um link para redefinir sua senha.",
      sendResetLink: "Enviar link",
      sending: "Enviando...",
      emailSent: "Email enviado!",
      emailSentDesc: "Se existir uma conta com este email, você receberá um link de redefinição.",
      backToLogin: "Voltar ao login",
      close: "Fechar"
    }
  };

  const pt = pageTexts[i18n.language] || pageTexts.fr;

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register state
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [registerCity, setRegisterCity] = useState('');
  const [registerCountry, setRegisterCountry] = useState('');

  const handleGoogleLogin = async () => {
    // Native Google OAuth - redirect to backend which handles the OAuth flow
    try {
      const response = await fetch(`${API}/auth/google`);
      const data = await response.json();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error('Google login unavailable');
      }
    } catch (err) {
      console.error('Google login error:', err);
      toast.error('Google login error');
    }
  };

  const handleFacebookLogin = async () => {
    try {
      const response = await fetch(`${API}/auth/facebook`, {
        method: 'GET',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error('Facebook login unavailable');
      }
    } catch (err) {
      console.error('Facebook login error:', err);
      toast.error('Facebook login error');
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      const response = await fetch(`${API}/auth/microsoft`, {
        method: 'GET',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error('Microsoft login unavailable');
      }
    } catch (err) {
      console.error('Microsoft login error:', err);
      toast.error('Microsoft login error');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error(pt.fillAllFields);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword
      }, {
        withCredentials: true
      });

      toast.success(pt.loginSuccess);
      await login(response.data); // Store user and token
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || pt.loginError);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error(pt.fillAllFields);
      return;
    }

    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: forgotEmail
      });
      setForgotSuccess(true);
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(error.response?.data?.detail || "Une erreur s'est produite");
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotSuccess(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerName || !registerEmail || !registerPassword || !confirmPassword) {
      toast.error(pt.fillAllFields);
      return;
    }

    if (registerPassword !== confirmPassword) {
      toast.error(pt.passwordsNotMatch);
      return;
    }

    if (registerPassword.length < 6) {
      toast.error(pt.passwordMinLength);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: registerName,
        email: registerEmail,
        password: registerPassword,
        city: registerCity || null,
        country: registerCountry || null
      }, {
        withCredentials: true
      });

      toast.success(pt.registerSuccess);
      await login(response.data); // Store user and token
      navigate('/dashboard');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || pt.registerError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12"
      style={{ background: 'linear-gradient(135deg, #004E89 0%, #2A9D8F 100%)' }}
      data-testid="auth-page"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-md w-full"
      >
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-6">
            <span className="text-white font-bold text-4xl">E</span>
          </div>
          <h1 className="text-3xl font-bold text-primary mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('auth.welcome')}
          </h1>
          <p className="text-gray-600">{t('auth.pleaseLogin')}</p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="login">{pt.login}</TabsTrigger>
            <TabsTrigger value="register">{pt.register}</TabsTrigger>
          </TabsList>

          {/* Login Tab */}
          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-email">{pt.email}</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="login-email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="pl-10 h-12"
                    placeholder={pt.emailPlaceholder}
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="login-password">{pt.password}</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="login-password"
                    type={showLoginPassword ? "text" : "password"}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="pl-10 pr-10 h-12"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-lg font-semibold rounded-full"
                data-testid="login-submit-button"
              >
                {loading ? t('common.loading') : pt.loginButton}
              </Button>

              {/* Forgot Password Link */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-primary hover:text-primary/80 hover:underline transition-colors"
                >
                  {pt.forgotPassword}
                </button>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">{pt.orContinueWith}</span>
                </div>
              </div>

              <div className="space-y-3 mt-4">
                {/* Google Login */}
                <Button
                  onClick={handleGoogleLogin}
                  variant="outline"
                  className="w-full h-12 text-lg font-semibold rounded-full border-2 hover:bg-gray-50"
                  data-testid="google-login-button"
                >
                  <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </Button>

                {/* Facebook Login */}
                <Button
                  variant="outline"
                  className="w-full h-12 text-lg font-semibold rounded-full border-2 hover:bg-blue-50 hover:border-blue-400 transition-all"
                  onClick={handleFacebookLogin}
                  disabled={socialLoading}
                >
                  <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="#1877F2">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </Button>

                {/* Microsoft Login */}
                <Button
                  variant="outline"
                  className="w-full h-12 text-lg font-semibold rounded-full border-2 hover:bg-blue-50 opacity-50 cursor-not-allowed"
                  onClick={() => toast.info('Microsoft login sera disponible prochainement / Coming soon')}
                  disabled
                  data-testid="microsoft-login-btn"
                  title="Coming soon / Bientôt disponible"
                >
                  <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
                    <path fill="#F25022" d="M1 1h10v10H1z"/>
                    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                    <path fill="#FFB900" d="M13 13h10v10H13z"/>
                  </svg>
                  Microsoft
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Register Tab */}
          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <Label htmlFor="register-name">{pt.name}</Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="register-name"
                    type="text"
                    value={registerName}
                    onChange={(e) => setRegisterName(e.target.value)}
                    className="pl-10 h-12"
                    placeholder={pt.namePlaceholder}
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="register-email">{pt.email}</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="register-email"
                    type="email"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    className="pl-10 h-12"
                    placeholder={pt.emailPlaceholder}
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              {/* City and Country fields for social proof */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="register-city">{pt.city}</Label>
                  <div className="relative mt-2">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="register-city"
                      type="text"
                      value={registerCity}
                      onChange={(e) => setRegisterCity(e.target.value)}
                      className="pl-10 h-12"
                      placeholder={pt.cityPlaceholder}
                      data-testid="register-city-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-country">{pt.country}</Label>
                  <div className="relative mt-2">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="register-country"
                      type="text"
                      value={registerCountry}
                      onChange={(e) => setRegisterCountry(e.target.value)}
                      className="pl-10 h-12"
                      placeholder={pt.countryPlaceholder}
                      data-testid="register-country-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="register-password">{pt.password}</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="register-password"
                    type={showRegisterPassword ? "text" : "password"}
                    value={registerPassword}
                    onChange={(e) => setRegisterPassword(e.target.value)}
                    className="pl-10 pr-10 h-12"
                    placeholder="••••••••"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showRegisterPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Minimum 6 caractères</p>
              </div>

              <div>
                <Label htmlFor="confirm-password">{pt.confirmPassword}</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 h-12"
                    placeholder="••••••••"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 text-lg font-semibold rounded-full"
                data-testid="register-submit-button"
              >
                {loading ? t('common.loading') : pt.registerButton}
              </Button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">{pt.orContinueWith}</span>
                </div>
              </div>

              <div className="space-y-3 mt-4">
                {/* Google Login */}
                <Button
                  onClick={handleGoogleLogin}
                  variant="outline"
                  className="w-full h-12 text-lg font-semibold rounded-full border-2 hover:bg-gray-50"
                >
                  <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </Button>

                {/* Facebook Login */}
                <Button
                  variant="outline"
                  className="w-full h-12 text-lg font-semibold rounded-full border-2 hover:bg-blue-50 hover:border-blue-400 transition-all"
                  onClick={handleFacebookLogin}
                  disabled={socialLoading}
                >
                  <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="#1877F2">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </Button>

                {/* Microsoft Login */}
                <Button
                  variant="outline"
                  className="w-full h-12 text-lg font-semibold rounded-full border-2 hover:bg-blue-50 opacity-50 cursor-not-allowed"
                  onClick={() => toast.info('Microsoft login sera disponible prochainement / Coming soon')}
                  disabled
                  data-testid="microsoft-login-btn"
                  title="Coming soon / Bientôt disponible"
                >
                  <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
                    <path fill="#F25022" d="M1 1h10v10H1z"/>
                    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                    <path fill="#FFB900" d="M13 13h10v10H13z"/>
                  </svg>
                  Microsoft
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Forgot Password Modal */}
      <AnimatePresence>
        {showForgotPassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={closeForgotPassword}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={closeForgotPassword}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {!forgotSuccess ? (
                <>
                  {/* Form */}
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Lock className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{pt.forgotPasswordTitle}</h2>
                    <p className="text-gray-600 mt-2 text-sm">{pt.forgotPasswordDesc}</p>
                  </div>

                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <Label htmlFor="forgot-email">{pt.email}</Label>
                      <div className="relative mt-2">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                          id="forgot-email"
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          className="pl-10 h-12"
                          placeholder={pt.emailPlaceholder}
                          required
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full bg-primary text-white hover:bg-primary/90 h-12 rounded-full font-semibold"
                    >
                      {forgotLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {pt.sending}
                        </>
                      ) : (
                        pt.sendResetLink
                      )}
                    </Button>
                  </form>

                  <button
                    onClick={closeForgotPassword}
                    className="w-full mt-4 text-gray-600 hover:text-gray-800 text-sm flex items-center justify-center gap-2 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {pt.backToLogin}
                  </button>
                </>
              ) : (
                <>
                  {/* Success state */}
                  <div className="text-center py-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", delay: 0.2 }}
                    >
                      <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{pt.emailSent}</h2>
                    <p className="text-gray-600 mb-6">{pt.emailSentDesc}</p>
                    <Button
                      onClick={closeForgotPassword}
                      className="bg-primary text-white hover:bg-primary/90 rounded-full px-8"
                    >
                      {pt.close}
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Auth;